import { getOrganizationAdminOrganizationIds } from 'shared/domain/permissions/getClientAdminPermission';
import {
  isBelowSuperAdminPermission,
  UserPermissionDto,
} from 'shared/types/userRole';

function areOrganizationAdminSetsEqual(
  previous: Set<string>,
  current: Set<string>
): boolean {
  if (previous.size !== current.size) {
    return false;
  }
  for (const id of previous) {
    if (!current.has(id)) {
      return false;
    }
  }
  return true;
}

/**
 * Guard: has org admin access changed between permission snapshots.
 * First snapshot (previous undefined) is initialization, not a change.
 */
export function hasOrgAdminAccessChanged(
  previous: UserPermissionDto | undefined,
  current: UserPermissionDto
): boolean {
  if (!previous) return false;

  return !areOrganizationAdminSetsEqual(
    getOrganizationAdminOrganizationIds(previous),
    getOrganizationAdminOrganizationIds(current)
  );
}

/**
 * Guard: has user become super admin.
 * First snapshot (previous undefined) is initialization, not a change.
 */
export function hasBecameSuperAdmin(
  previous: UserPermissionDto | undefined,
  current: UserPermissionDto
): boolean {
  if (!previous) return false;

  const wasSuperAdmin = !isBelowSuperAdminPermission(previous);
  const isSuperAdmin = !isBelowSuperAdminPermission(current);
  return !wasSuperAdmin && isSuperAdmin;
}

/**
 * Guard: has user lost super admin role.
 * First snapshot (previous undefined) is initialization, not a change.
 */
export function hasLostSuperAdmin(
  previous: UserPermissionDto | undefined,
  current: UserPermissionDto
): boolean {
  if (!previous) return false;

  const wasSuperAdmin = !isBelowSuperAdminPermission(previous);
  const isSuperAdmin = !isBelowSuperAdminPermission(current);
  return wasSuperAdmin && !isSuperAdmin;
}
